import { API_TIMEOUT, API_ENDPOINT } from 'settings';

import { ApiError } from 'shared/errors/ApiError';
import { Observable } from 'rxjs/Observable';
import axios, { AxiosRequestConfig, Method } from 'axios';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';

export interface IApiOpts {
  local?: boolean;
  maxAttempts?: number;
}

const internetStatus$ = new BehaviorSubject(true);

window.addEventListener('offline', () => {
  internetStatus$.next(false);
});

window.addEventListener('online', () => {
  internetStatus$.next(true);
});

export function getInternetStatus(): Observable<boolean> {
  return internetStatus$.asObservable().distinctUntilChanged();
}

function request<T>(method: Method, url: string, data: any = null, extra: AxiosRequestConfig = {}, opt: IApiOpts = {}): Observable<T> {
  extra = extra || {};

  extra.headers = extra.headers || { 'Content-Type': 'application/json' };

  if (!opt.local && !url.match(/^https?:\/\//)) {
    url = `${API_ENDPOINT}${url}`;
  }

  let attempts = 0;
  const maxAttempts = opt.maxAttempts || 60;

  return Observable.of(true)
    .switchMap(() => {
      attempts++;

      return Observable.fromPromise(
        axios.request({
          url,
          method,
          timeout: API_TIMEOUT,
          params: method === 'GET' ? data : null,
          data: ['POST', 'PUT'].includes(method) ? data : null,
          responseType: 'json',
          ...extra
        })
      );
    })
    .map(response => {
      if (response.data === null) {
        throw {
          message: 'Invalid server response',
          config: response.config,
          response,
          err: response
        };
      }

      internetStatus$.next(true);

      return response.data;
    })
    .retryWhen(err$ => {
      return err$.switchMap(err => {
        console.log(err.message);
        if (err.message === 'no-internet' || (err.message === 'Network Error' && attempts < maxAttempts)) {
          internetStatus$.next(false);
          return Observable.of(true).delay(2000);
        }

        return Observable.throw(err);
      });
    })
    .catch(err => {
      const code = (err.response && err.response.status) || 0;

      if ([204].includes(code)) {
        return Observable.of(null);
      }

      return Observable.throw(new ApiError(err.config, err.response, err));
    });
}

export function get<T = any>(url: string, params?: any, extra: AxiosRequestConfig = {}, opt: IApiOpts = {}): Observable<T> {
  return request('GET', url, params, extra, opt);
}

export function post<T = any>(url: string, body: any, extra: AxiosRequestConfig = {}, opt: IApiOpts = {}): Observable<T> {
  return request('POST', url, body, extra, opt);
}

export function put<T = any>(url: string, body: any, extra: AxiosRequestConfig = {}, opt: any = {}): Observable<T> {
  return request('PUT', url, body, extra, opt);
}

export function del<T = any>(url: string, params?: any, extra: AxiosRequestConfig = {}, opt: IApiOpts = {}): Observable<T> {
  return request('DELETE', url, params, extra, opt);
}
